"use client";

import useEmblaCarousel from "embla-carousel-react";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import type { EmblaCarouselType } from "embla-carousel";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn, formatSectionTitle } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

interface InsightItem {
  id?: number | string;
  meta_id?: number;
  /** Stat illustration shown on the right of the card (150×150) */
  meta_image?: string;
  /** Source / publisher logo shown at the top-left of the card (88×58) */
  meta_hover_image?: string;
  meta_alt_image?: string;
  /** Wrap a phrase in { } to render it bold on the red→pink highlight band */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean | number;
}

interface AcademicDeliverables1006Data {
  title?: string;
  title_text_color?: string;
  /** Overrides the {…} span color in the title; empty = CU red */
  highlight_color?: string;
  button_text?: string;
  button_text_color?: string;
  button_background_color?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  metadata?: InsightItem[];
  status?: boolean | number;
}

// CU theme constants baked into the source organism (Arrow3 icon + card tokens)
const CU_RED = "#E1473F";
const ARROW_DISABLED_BG = "#FAE1E0";
const DESCRIPTION_HIGHLIGHT_GRADIENT =
  "linear-gradient(90deg, #E1473F 0%, #FFE3E2 100%)";

const CarouselArrow = ({
  direction,
  disabled,
  onClick,
  className,
  iconClassName,
}: {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
  className?: string;
  iconClassName?: string;
}) => (
  <button
    type="button"
    aria-label={direction === "prev" ? "Previous insight" : "Next insight"}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "rounded-full shadow-[0px_4px_4px_0px_#00000040]",
      disabled ? "cursor-not-allowed" : "cursor-pointer",
      className
    )}
  >
    {/* Inline port of CU's Arrow3 icon (filled circle + chevron) */}
    <svg
      viewBox="0 0 64 63"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(direction === "prev" && "rotate-180", iconClassName)}
    >
      <rect
        width="63.9884"
        height="62.3015"
        rx="31.1507"
        fill={disabled ? ARROW_DISABLED_BG : CU_RED}
      />
      <path
        d="M25.5348 42.7668C25.1923 42.4242 24.9999 41.9596 24.9999 41.4752C24.9999 40.9907 25.1923 40.5261 25.5348 40.1835L34.5784 31.14L25.5348 22.0965C25.202 21.7519 25.0179 21.2904 25.022 20.8114C25.0262 20.3323 25.2183 19.8741 25.5571 19.5354C25.8958 19.1966 26.354 19.0045 26.8331 19.0003C27.3121 18.9962 27.7736 19.1803 28.1182 19.5131L38.4534 29.8483C38.7959 30.1909 38.9883 30.6555 38.9883 31.14C38.9883 31.6244 38.7959 32.089 38.4534 32.4316L28.1182 42.7668C27.7756 43.1093 27.3109 43.3018 26.8265 43.3018C26.3421 43.3018 25.8774 43.1093 25.5348 42.7668Z"
        fill={disabled ? CU_RED : "#FFFFFF"}
      />
    </svg>
  </button>
);

const InsightCard = ({
  item,
  className,
  cardRef,
}: {
  item: InsightItem;
  className?: string;
  cardRef?: (el: HTMLDivElement | null) => void;
}) => (
  <div
    ref={cardRef}
    className={cn(
      "flex h-full w-[320px] flex-col items-center justify-between gap-7 rounded-[10px]",
      "border border-[#D1D5DB4D] bg-[#FFFFFF] px-6 py-9 shadow-[0px_4px_24px_0px_#00000021]",
      "min-[1024px]:w-[542px] min-[1024px]:flex-row min-[1024px]:gap-4 min-[1024px]:p-6",
      className
    )}
  >
    <div className="flex h-full flex-1 flex-col items-center justify-center gap-[10px] min-[1024px]:items-start min-[1024px]:justify-start">
      {item.meta_hover_image && (
        <Image
          src={item.meta_hover_image}
          alt={item.meta_alt_image || "Source logo"}
          width={88}
          height={58}
        />
      )}
      <div
        className="text-center text-[16px] font-normal leading-[1.5] text-[#171719] min-[1024px]:w-[284px] min-[1024px]:text-left"
        dangerouslySetInnerHTML={{
          // Inline-style the highlight band: an admin-tab stylesheet loaded
          // before this widget existed won't contain a freshly generated
          // arbitrary utility, so a class can't be trusted here.
          __html: formatSectionTitle(item.meta_description || "", "").replace(
            /<span /g,
            `<span style="font-weight:700;background-image:${DESCRIPTION_HIGHLIGHT_GRADIENT}" `
          ),
        }}
      />
    </div>
    {item.meta_image && (
      <div className="flex-shrink-0">
        <Image
          src={item.meta_image}
          alt={item.meta_alt_image || "Industry insight"}
          width={150}
          height={150}
          className="h-[150px] w-[150px]"
        />
      </div>
    )}
  </div>
);

const AcademicDeliverables1006 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const elementRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const didAutoAdvance = useRef(false);
  const [isCtaHovered, setIsCtaHovered] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // The source organism switches carousels at 1024px; null (SSR) renders desktop
  const isMobileView = useIsMobile(1024) === true;

  // Handle both single and double nesting from PreviewRenderer
  const sectionData: AcademicDeliverables1006Data = data?.data ?? data ?? {};

  const {
    title = "",
    title_text_color = "#000000",
    highlight_color = "",
    button_text = "Get Started",
    button_text_color = "#FFFFFF",
    button_background_color = "",
    desktop_bg_image = "",
    mobile_bg_image = "",
    metadata = [],
  } = sectionData;

  // Active insight cards, sorted by priority (mirrors AcademicDeliverables1002's
  // metadata handling).
  const insights = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item) => item.meta_status)
      .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0));
  }, [metadata]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    loop: false,
  });

  const updateScrollEdges = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const syncEdges = () => updateScrollEdges(emblaApi);
    syncEdges();
    emblaApi.on("select", syncEdges);
    emblaApi.on("reInit", syncEdges);
    return () => {
      emblaApi.off("select", syncEdges);
      emblaApi.off("reInit", syncEdges);
    };
  }, [emblaApi, updateScrollEdges]);

  // The source carousel advances once on mount (>2 slides) so the second card
  // lands centered under align:"center".
  useEffect(() => {
    if (!emblaApi || isMobileView || insights.length <= 2) return;
    if (didAutoAdvance.current) return;
    didAutoAdvance.current = true;
    const timer = setTimeout(() => emblaApi.scrollNext(), 0);
    return () => clearTimeout(timer);
  }, [emblaApi, isMobileView, insights.length]);

  // Desktop only: fully visible cards at full opacity, clipped ones dimmed
  useEffect(() => {
    if (isMobileView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          target.style.opacity =
            entry.isIntersecting && entry.intersectionRatio === 1 ? "1" : "0.2";
          target.style.transition = "opacity 0.7s ease-out";
        });
      },
      { threshold: 1 }
    );
    cardRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });
    return () => observer.disconnect();
  }, [isMobileView, insights.length]);

  useEffect(() => {
    if (elementRef.current) {
      FireWidgetViewedEvent(elementRef, "industry_insights");
    }
  }, []);

  const active = sectionData.status === true || sectionData.status === 1;
  if (!active) return null;
  if (!title && insights.length === 0) return null;

  const backgroundImage = isMobileView
    ? mobile_bg_image || desktop_bg_image
    : desktop_bg_image || mobile_bg_image;
  const highlight = highlight_color || CU_RED;
  const isSingleSlide = insights.length === 1;

  return (
    <section
      ref={elementRef}
      className="flex items-center justify-center bg-cover bg-center bg-no-repeat [&_*]:![font-family:Poppins,sans-serif]"
      style={{
        fontFamily: "Poppins, sans-serif",
        backgroundImage: backgroundImage
          ? `url("${backgroundImage}")`
          : undefined,
      }}
      data-widget="academic-deliverables-1006"
    >
      <div className="flex w-full flex-col gap-[56px] pb-[60px] pt-9 min-[768px]:pb-[100px] min-[768px]:pt-[36px] min-[1024px]:w-screen">
        <div className="flex flex-col items-center gap-4">
          {title && (
            <h2
              className="text-center text-[32px] font-semibold leading-normal"
              style={{ color: title_text_color }}
              dangerouslySetInnerHTML={{
                __html: formatSectionTitle(title, "").replace(
                  /<span /g,
                  `<span style="color:${highlight};white-space:normal" `
                ),
              }}
            />
          )}
          <div className="min-[768px]:w-fit">
            <button
              type="button"
              onClick={() => {
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  })
                );
              }}
              onMouseEnter={() => setIsCtaHovered(true)}
              onMouseLeave={() => setIsCtaHovered(false)}
              className="inline-flex cursor-pointer items-center justify-center gap-2.5 whitespace-nowrap rounded-md px-[18px] py-3 text-[16px] font-semibold"
              style={{
                color: button_text_color,
                backgroundColor: button_background_color || CU_RED,
              }}
            >
              {button_text || "Get Started"}
              <span className="size-6">
                <ArrowUpRight
                  className={cn(
                    "transition-transform duration-300",
                    isCtaHovered ? "rotate-[45deg]" : "rotate-0"
                  )}
                />
              </span>
            </button>
          </div>
        </div>

        {insights.length > 0 && (
          <div>
            {isMobileView ? (
              /* Mobile: index slider ported from CU's Carousel5, with the
                 prev/next arrows stacked in a centered row below the card */
              <div className="flex w-full flex-col items-center gap-6 px-4 pb-2">
                <div className="relative w-full overflow-hidden">
                  <div
                    className="flex transition-transform duration-700 ease-in-out"
                    style={{
                      transform: `translateX(-${mobileIndex * 100}%)`,
                    }}
                  >
                    {insights.map((item, index) => (
                      <div
                        key={item.meta_id ?? item.id ?? `insight-${index}`}
                        className="flex w-full flex-shrink-0 justify-center"
                      >
                        <div className="flex w-full max-w-[332px] justify-center min-[768px]:max-w-[400px]">
                          <InsightCard item={item} className="w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {!isSingleSlide && (
                  <div className="flex items-center justify-center gap-6">
                    <CarouselArrow
                      direction="prev"
                      disabled={mobileIndex === 0}
                      onClick={() => setMobileIndex((i) => Math.max(i - 1, 0))}
                      iconClassName="size-[45px] transition-all duration-300 ease-in-out"
                    />
                    <CarouselArrow
                      direction="next"
                      disabled={mobileIndex === insights.length - 1}
                      onClick={() =>
                        setMobileIndex((i) =>
                          Math.min(i + 1, insights.length - 1)
                        )
                      }
                      iconClassName="size-[45px] transition-all duration-300 ease-in-out"
                    />
                  </div>
                )}
              </div>
            ) : (
              /* Desktop: embla carousel ported from CU's TestimonialsCarousel */
              <div className="mx-auto h-fit w-full max-w-7xl px-2">
                <div className="relative">
                  {!isSingleSlide && (
                    <CarouselArrow
                      direction="prev"
                      disabled={!canScrollPrev}
                      onClick={() => emblaApi?.scrollPrev()}
                      className="absolute left-0 top-1/2 z-10 -translate-y-1/2 disabled:opacity-50"
                      iconClassName="size-[63px]"
                    />
                  )}

                  <div
                    className={cn(
                      "overflow-hidden",
                      isSingleSlide ? "px-0" : "px-24"
                    )}
                    ref={emblaRef}
                  >
                    <div
                      className={cn("flex", isSingleSlide && "justify-center")}
                    >
                      {insights.map((item, index) => (
                        <div
                          key={item.meta_id ?? item.id ?? `insight-${index}`}
                          className={cn(
                            "shrink-0",
                            isSingleSlide ? "max-w-full flex-[0_0_auto]" : "mx-2"
                          )}
                        >
                          <InsightCard
                            item={item}
                            className="mx-[40px] h-full"
                            cardRef={(el) => {
                              cardRefs.current[index] = el;
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {!isSingleSlide && (
                    <CarouselArrow
                      direction="next"
                      disabled={!canScrollNext}
                      onClick={() => emblaApi?.scrollNext()}
                      className="absolute right-0 top-1/2 z-10 -translate-y-1/2 disabled:opacity-50"
                      iconClassName="size-[63px]"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AcademicDeliverables1006;
